import { ApiError, request } from './client'
import { parseDraft, parseDraftPhotos } from './threads'
import type { Draft, DraftPhoto } from './threads'
import { asObject, bool } from './parse'

/** What the server sends back once a decision is recorded. */
export interface Decision {
  readonly draft: Draft
  /**
   * Whether a send was actually scheduled.
   *
   * False after an approve means nothing in this process will carry the message.
   * The panel says so out loud: a manager who believes an answer is on its way
   * stops watching the conversation.
   */
  readonly queued: boolean
}

/**
 * The decision lost its race: the draft moved between the screen being drawn and
 * the button being pressed.
 *
 * This is a separate type from `ApiError` because it demands a different
 * response. Every other failure may be retried; this one may not — retrying it
 * would approve an answer to a conversation that has since changed, which is
 * precisely what the version guard exists to prevent. `draft` carries the row as
 * it now stands, so the screen can show what it became without asking again and
 * losing the same race twice.
 */
export class VersionConflictError extends Error {
  constructor(
    readonly draft: Draft | null,
    message: string,
  ) {
    super(message)
    this.name = 'VersionConflictError'
  }
}

function decision(body: unknown): Decision {
  const source = asObject(body, 'the decision')
  return { draft: parseDraft(source['draft']), queued: bool(source, 'queued') }
}

/**
 * Turn a 409 into the conflict type, and leave everything else alone.
 *
 * The code is checked rather than the status: a 409 without
 * `code: "version_conflict"` is some other kind of conflict and has no draft to
 * show, and treating it as one would put an empty "look again" panel on screen.
 */
function asConflict(error: unknown): unknown {
  if (!(error instanceof ApiError) || error.status !== 409) return error
  if (typeof error.body !== 'object' || error.body === null) return error

  const source = error.body as Record<string, unknown>
  if (source['code'] !== 'version_conflict') return error

  let draft: Draft | null = null
  try {
    draft = parseDraft(source['draft'])
  } catch {
    // The refusal stands with or without the row attached; a body we cannot
    // read must not turn a conflict back into a retryable error.
    draft = null
  }
  return new VersionConflictError(draft, error.message)
}

export interface ApproveInput {
  readonly draftId: string
  /** The version the manager was reading. Never re-read from a fresher copy. */
  readonly version: number
  /** The manager's own wording, or null to send the model's. */
  readonly editedBody: string | null
}

export async function approveDraft(input: ApproveInput): Promise<Decision> {
  try {
    return await request(`/api/drafts/${encodeURIComponent(input.draftId)}/approve`, decision, {
      method: 'POST',
      body: { version: input.version, edited_body: input.editedBody },
    })
  } catch (error) {
    throw asConflict(error)
  }
}

export interface RejectInput {
  readonly draftId: string
  readonly version: number
  readonly reason: string
}

export async function rejectDraft(input: RejectInput): Promise<Decision> {
  try {
    return await request(`/api/drafts/${encodeURIComponent(input.draftId)}/reject`, decision, {
      method: 'POST',
      body: { version: input.version, reason: input.reason },
    })
  } catch (error) {
    throw asConflict(error)
  }
}

/**
 * Which pictures go with the reply.
 *
 *   PUT /api/drafts/{id}/photos    drafts.approve
 *
 * The whole list every time, in the order they should arrive: the manager is
 * looking at a row of photographs and saying what it should be, and a merge
 * would keep the one they removed. Only a pending draft accepts this — an
 * approved one is on its way and its pictures were part of what was approved.
 */
export async function setDraftPhotos(
  draftId: string,
  assetIds: readonly string[],
): Promise<readonly DraftPhoto[]> {
  return request(
    `/api/drafts/${encodeURIComponent(draftId)}/photos`,
    (body) => parseDraftPhotos(asObject(body, 'the pictures').photos),
    { method: 'PUT', body: { asset_ids: assetIds } },
  )
}
